import { computed, nextTick, ref, type ComputedRef, type Ref } from 'vue';
import { applyTableFilters, type NxTableColumnFilter, type NxTableFilterKind } from './filterModel';
import type { TableMetadataState } from './internal';

/**
 * Client-side sort + header filters for an `NxTable` fed a plain `:data`
 * array the page loaded itself.
 *
 * `NxTable` never sorts or filters `data`: a header click only records the new
 * state and hands it to `onTableStateChange` — the consumer owns the rows. A
 * table without that callback therefore shows a sort arrow and a filter input
 * that change nothing. Server-backed grids turn the state into a query
 * (`tableStateToServerQuery`); a page that holds all its rows in memory sorts
 * and filters them here instead.
 *
 * The loaded rows ARE the universe: with a capped request, sorting and
 * filtering reorder and narrow exactly the rows that were loaded, never the
 * ones beyond the cap.
 *
 * ```ts
 * const { visibleRows, onTableStateChange } = useNxTableClientState(rows, {
 *   kinds: new Map([['amount', 'currency']]),
 * });
 * ```
 * then bind `:data="visibleRows"` and `:on-table-state-change="onTableStateChange"`.
 */
export type NxTableClientStateOptions<T> = {
  /**
   * The filter/sort semantics per column, keyed by `columnId`. Must mirror the
   * column components (`NxTableStringColumn` → `'string'`, `NxTableDateColumn`
   * → `'date'`, `NxTableCurrencyColumn` → `'currency'`, `NxTableNumberColumn`
   * → `'number'`): the table's state carries only ids, orders and priorities,
   * never the kinds. An unlisted column compares as a string.
   */
  kinds?: ReadonlyMap<string, NxTableFilterKind>;
  /**
   * The raw value a column sorts by, when the field it renders is display
   * text (`'15.09.2026 14:33'` sorts as nonsense by its characters). Return
   * `undefined` to fall back to `row[columnId]`. Filtering does NOT use this:
   * the user filters what they can read in the cell.
   */
  sortValue?: (row: T, columnId: string) => unknown;
  /** Collation for text columns. Default `'de'` — `Ä` sorts next to `A`, not after `Z`. */
  locale?: string;
};

/**
 * Comparable projection of one cell — the ordering the kit's filter evaluator
 * uses. `null` means „no value“ and sorts FIRST ascending (last descending).
 */
function comparable(kind: NxTableFilterKind, raw: unknown): number | string | null {
  if (raw === null || raw === undefined || raw === '') return null;
  switch (kind) {
    case 'number':
    case 'currency': {
      // Amounts often ride as decimal STRINGS so nothing rounds on the way;
      // compared as text, '1200.00' would sort before '900.00'.
      const value = typeof raw === 'number' ? raw : Number(String(raw).replace(',', '.'));
      return Number.isFinite(value) ? value : null;
    }
    case 'date': {
      const time = raw instanceof Date ? raw.getTime() : Date.parse(String(raw));
      return Number.isFinite(time) ? time : null;
    }
    case 'bool':
      return raw === true || raw === 'true' || raw === 1 || raw === '1' ? 1 : 0;
    default:
      return String(raw);
  }
}

function compareCells(kind: NxTableFilterKind, a: unknown, b: unknown, locale: string): number {
  const left = comparable(kind, a);
  const right = comparable(kind, b);
  if (left === null && right === null) return 0;
  if (left === null) return -1;
  if (right === null) return 1;
  if (typeof left === 'number' && typeof right === 'number') return left - right;
  return String(left).localeCompare(String(right), locale, { numeric: true });
}

/**
 * Applies one `NxTable` state to a client-side row array: every filter layer
 * first (quick filter row, header-filter conditions, the wizard's tree), then
 * the active sorts in priority order. The input array is never mutated; equal
 * rows keep their loaded order.
 */
export function applyTableClientState<T extends Record<string, unknown>>(
  rows: readonly T[],
  state: TableMetadataState<T>,
  options: NxTableClientStateOptions<T> = {}
): T[] {
  const kinds = options.kinds;
  const kindOf = (columnId: string): NxTableFilterKind => kinds?.get(columnId) ?? 'string';
  const locale = options.locale ?? 'de';

  const filtered = applyTableFilters(rows, {
    // `TableMetadataState` keys its maps by `keyof T | (string & {})`; with
    // `T extends Record<string, unknown>` those keys are strings.
    quickFilters: state.filterValues as ReadonlyMap<string, readonly unknown[]>,
    columnFilters: state.columnFilters as ReadonlyMap<string, NxTableColumnFilter>,
    advancedFilter: state.advancedFilter,
    filterModifier: state.filterModifier,
    kinds,
  });

  // `sortPrio` is 0-based and 0 is the HIGHEST priority.
  const sorts = state.columns
    .filter((column) => column.sortOrder != null)
    .sort((a, b) => (a.sortPrio ?? 0) - (b.sortPrio ?? 0));
  if (sorts.length === 0) return filtered;

  const valueOf = (row: T, columnId: string): unknown => {
    const raw = options.sortValue?.(row, columnId);
    return raw === undefined ? row[columnId] : raw;
  };

  // `filtered` is a fresh array, so sorting in place never touches the
  // caller's rows; `Array.prototype.sort` is stable.
  return filtered.sort((a, b) => {
    for (const sort of sorts) {
      const columnId = String(sort.columnId);
      const cmp = compareCells(
        kindOf(columnId),
        valueOf(a, columnId),
        valueOf(b, columnId),
        locale
      );
      if (cmp !== 0) return sort.sortOrder === 'desc' ? -cmp : cmp;
    }
    return 0;
  });
}

export type NxTableClientState<T extends Record<string, unknown>> = {
  /** Bind to `:data` — the loaded rows, filtered and sorted. */
  visibleRows: ComputedRef<T[]>;
  /** Bind to `:on-table-state-change`. */
  onTableStateChange: (state: TableMetadataState<T>) => Promise<void>;
  /** Whether the user has put the table into an order of their own. */
  sortActive: ComputedRef<boolean>;
  /** Whether any filter layer currently narrows the rows. */
  filterActive: ComputedRef<boolean>;
  /** Forgets the table state — back to the loaded order, unfiltered. */
  reset: () => void;
};

export function useNxTableClientState<T extends Record<string, unknown>>(
  rows: Ref<readonly T[]> | ComputedRef<readonly T[]>,
  options: NxTableClientStateOptions<T> = {}
): NxTableClientState<T> {
  const tableState = ref<TableMetadataState<T> | null>(null) as Ref<TableMetadataState<T> | null>;

  // A computed, not a snapshot taken at the last header click: pages reload
  // their rows on their own triggers, and a snapshot would go stale or drop
  // the user's sort on every reload.
  const visibleRows = computed<T[]>(() =>
    tableState.value === null
      ? [...rows.value]
      : applyTableClientState(rows.value, tableState.value, options)
  );

  const sortActive = computed(
    () => tableState.value?.columns.some((column) => column.sortOrder != null) ?? false
  );

  const filterActive = computed(() => {
    const state = tableState.value;
    if (state === null) return false;
    return (
      state.filterValues.size > 0 || state.columnFilters.size > 0 || state.advancedFilter !== null
    );
  });

  async function onTableStateChange(state: TableMetadataState<T>): Promise<void> {
    tableState.value = state;
    // `NxTable` awaits this before it re-reads `data`; `visibleRows` is
    // already current, one tick lets the render settle.
    await nextTick();
  }

  return {
    visibleRows,
    onTableStateChange,
    sortActive,
    filterActive,
    reset: () => {
      tableState.value = null;
    },
  };
}
